import React, { useEffect, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { fetchPublicSettings, pickCmsShort } from '../services/publicContentService';

const DEFAULT_VIDEO = 'https://customer-assets.emergentagent.com/job_ebc-depths/artifacts/djaocnom_11279748-hd_1920_1080_30fps.mp4';

const Diver = () => {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const [cms, setCms] = useState({});
  useEffect(() => {
    let alive = true;
    fetchPublicSettings('diver').then((v) => { if (alive) setCms(v || {}); });
    return () => { alive = false; };
  }, []);

  const line1    = pickCmsShort(cms.title_line1, isAr) || (isAr ? 'نستكشف الأعماق' : 'Exploring the Depths');
  const line2    = pickCmsShort(cms.title_line2, isAr) || (isAr ? 'لحماية المستقبل' : 'to Protect the Future');
  const subtitle = pickCmsShort(cms.subtitle,    isAr) || (isAr
    ? 'فريق من الخبراء المتخصصين في حماية البيئة البحرية والبرية'
    : 'A team of experts specializing in the protection of marine and terrestrial environments');
  const videoUrl = (cms.video_url && String(cms.video_url).trim()) || DEFAULT_VIDEO;

  return (
    <section className="relative min-h-screen overflow-hidden flex items-center justify-center">
      {/* Video Background */}
      <div className="absolute inset-0 w-full h-full">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectFit: 'cover' }}
        >
          <source 
            src={videoUrl} 
            type="video/mp4" 
          />
        </video>

        {/* Dark blue overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-blue-950/60 via-blue-900/50 to-cyan-950/60 dark:from-black/70 dark:via-blue-950/60 dark:to-black/70" />
        
        {/* Additional subtle overlay for depth */}
        <div className="absolute inset-0 bg-blue-950/20" />
      </div>

      {/* Floating Particles for extra depth (subtle) */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-30">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-cyan-300/20"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              width: `${2 + Math.random() * 4}px`,
              height: `${2 + Math.random() * 4}px`,
              animation: `float-up ${6 + Math.random() * 4}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 5}s`
            }}
          />
        ))}
      </div>

      {/* Main Content - Centered Text */}
      <div className="relative z-20 container mx-auto px-4">
        <div className="max-w-6xl mx-auto text-center">
          <div className="space-y-8 animate-fade-in-up">
            {/* Main Title - Large and Cinematic (H1 — page hero) */}
            <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl font-bold leading-tight">
              <span data-testid="diver-title-1" className="block text-white drop-shadow-[0_0_40px_rgba(0,0,0,0.9)] drop-shadow-[0_4px_12px_rgba(0,0,0,0.8)]">
                {line1}
              </span>
              <span data-testid="diver-title-2" className="block text-cyan-300 drop-shadow-[0_0_40px_rgba(0,0,0,0.9)] drop-shadow-[0_4px_12px_rgba(0,0,0,0.8)] mt-4">
                {line2}
              </span>
            </h1>

            {/* Subtitle (H2 — proportional to hero headline) */}
            <p data-testid="diver-subtitle" className="text-2xl md:text-3xl lg:text-4xl text-cyan-50 max-w-4xl mx-auto drop-shadow-[0_0_30px_rgba(0,0,0,0.9)] drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)] animate-fade-in-up animation-delay-200 leading-relaxed font-medium">
              {subtitle}
            </p>

            {/* Decorative element */}
            <div className="flex items-center justify-center gap-4 animate-fade-in-up animation-delay-400 pt-4">
              <div className="h-1 w-24 bg-gradient-to-r from-transparent via-cyan-400 to-transparent rounded-full opacity-80" />
              <div className="w-3 h-3 bg-cyan-400 rounded-full animate-pulse shadow-lg shadow-cyan-400/50" />
              <div className="h-1 w-24 bg-gradient-to-l from-transparent via-cyan-400 to-transparent rounded-full opacity-80" />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom fade for smooth transition to next section */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-slate-50 dark:from-slate-900 to-transparent pointer-events-none z-10" />
    </section>
  );
};

export default Diver;
